import { useState, useEffect, type FormEvent } from 'react';
import { Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  formatPhoneToInternational,
  signInWithPhonePassword,
} from '@ubus/shared';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { translateAuthError } from '../i18n/translateErrors';

const REMEMBER_KEY = 'ubus-remember-phone';

function normalizePhone(raw: string): string {
  const trimmed = raw.trim().replace(/\s/g, '');
  if (!trimmed) return '';

  // Already international (+86 / +61 / …) or AU local (0…) — do not force +61.
  // Old logic used startsWith('+61'), which wrongly treated +86 as needing another +61 prefix.
  if (trimmed.startsWith('+') || trimmed.startsWith('0')) {
    return formatPhoneToInternational(trimmed);
  }

  // Bare national digits without country code: keep AU default for local driver habit.
  return formatPhoneToInternational(`+61${trimmed}`);
}

function Login() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const restoreSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) navigate('/', { replace: true });
    };

    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setPhone(saved.startsWith('+61') ? saved : `+61${saved.replace(/^0/, '')}`);
      }
    } catch {
      // ignore
    }

    restoreSession();
  }, [navigate]);

  const canSubmit = phone.trim().length > 0 && password.trim().length > 0;

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || loading) return;

    const normalized = normalizePhone(phone);

    if (!normalized) {
      Toast.show({ icon: 'fail', content: t('login.enterPhone') });
      return;
    }

    if (!password.trim()) {
      Toast.show({ icon: 'fail', content: t('login.enterPassword') });
      return;
    }

    localStorage.setItem(REMEMBER_KEY, normalized);

    setLoading(true);
    try {
      const { session, error } = await signInWithPhonePassword(normalized, password);

      if (error) {
        Toast.show({ icon: 'fail', content: translateAuthError(error, t, 'phone') });
      } else if (session) {
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      Toast.show({
        icon: 'fail',
        content: err instanceof Error ? err.message : t('login.signInFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="driver-login">
      <LanguageSwitcher />
      <form className="driver-login-main" onSubmit={handleLogin}>
        <div className="driver-login-brand">
          <span className="driver-login-brand-text">ubus</span>
          <svg className="driver-login-brand-swoosh" viewBox="0 0 78 10" fill="none" aria-hidden="true">
            <path
              d="M2 8C18 2 28 2 38 6C48 10 58 8 76 3"
              stroke="#2F80ED"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <h1 className="driver-login-heading">{t('login.welcome')}</h1>
        <p className="driver-login-subheading">{t('login.subtitle')}</p>

        <label className="driver-login-label" htmlFor="login-phone">{t('login.phone')}</label>
        <div className="driver-login-field-wrap">
          <input
            id="login-phone"
            className="driver-login-field"
            type="tel"
            inputMode="tel"
            autoComplete="username"
            placeholder={t('login.phonePlaceholder')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {phone.length > 0 && (
            <button
              type="button"
              className="driver-login-clear"
              aria-label={t('common.clear')}
              onClick={() => setPhone('')}
            >
              ×
            </button>
          )}
        </div>

        <label className="driver-login-label" htmlFor="login-password">{t('login.password')}</label>
        <div className="driver-login-field-wrap">
          <input
            id="login-password"
            className="driver-login-field"
            type="password"
            autoComplete="current-password"
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button
          type="submit"
          className={`driver-login-submit ${canSubmit ? 'driver-login-submit--active' : ''}`}
          disabled={!canSubmit || loading}
        >
          {t('login.submit')}
        </button>

        <p className="driver-login-forgot-hint">{t('login.forgotHint')}</p>
      </form>
    </div>
  );
}

export default Login;
